// Which parts of a training file get used: silence is found on the device
// (no upload needed) and cut automatically, and the person can cut more by
// hand (components/train/ClipEditor). What's left is sent with the file as
// `keep: [[start, end], ...]` seconds; the training job cuts the file to
// those ranges before it slices and cleans it (startTrainingFull).
// The length rule (MIN_TRAINING_SECONDS to MAX_TRAINING_SECONDS) counts kept
// seconds, not file length: silence and cut parts were counted before, so a
// 6-minute file with a minute of quiet trained on 5.

export type Range = [number, number];

// How much singing or speaking a voice needs, counting only what is kept and
// only unique audio (a repeated file is not added, see fingerprint below).
//
// The minimum was 6:00 counting repeats. Counting unique audio only, 35% of
// the voices trained 2026-09-09..10-09 would have been blocked at 6:00 - and
// voices with 3-6 minutes of unique audio came out about as well as 6-10
// (35.4% vs 31.4% bad outcomes). So 3:00 is the floor and 6:00 is advice, not
// a gate. Set MIN_TRAINING_SECONDS to 360 to go back to a hard 6:00; the
// "6+ minutes sounds better" note then never shows.
export const MIN_TRAINING_SECONDS = 180;
export const RECOMMENDED_TRAINING_SECONDS = 360;
export const MAX_TRAINING_SECONDS = 3600;

export type Analysis = {
  duration: number;
  // Loudness as bars, 0..1, for the waveform.
  peaks: number[];
  // Quiet stretches long enough to cut.
  silent: Range[];
};

// Analysed at 8 kHz: plenty for loudness, and a 60-minute file is ~115 MB of
// samples instead of ~1.4 GB at 48 kHz stereo.
const RATE = 8000;
const WINDOW_S = 0.05;
// Quiet for at least this long is silence (shorter gaps are breaths).
const MIN_SILENCE_S = 0.8;
// Kept around each sung part, so word starts and tails aren't clipped.
const PAD_S = 0.15;
// Kept parts shorter than this are dropped (clicks, a stray word).
const MIN_KEEP_S = 0.5;
// Bigger files keep the whole file rather than risk the tab running out of
// memory decoding it.
const MAX_ANALYSE_BYTES = 150 * 1024 * 1024;
const PEAK_BARS = 240;

export async function analyse(file: Blob): Promise<Analysis | null> {
  if (file.size > MAX_ANALYSE_BYTES) return null;
  try {
    const data = await file.arrayBuffer();
    // Safari's lowest supported rate is higher than other browsers'.
    let buffer: AudioBuffer | null = null;
    for (const rate of [RATE, 22050]) {
      try {
        const ctx = new OfflineAudioContext(1, rate, rate);
        buffer = await ctx.decodeAudioData(data.slice(0));
        break;
      } catch {
        // Try the next rate.
      }
    }
    if (!buffer) return null;
    const n = buffer.length;
    const channels = Array.from({ length: buffer.numberOfChannels }, (_, c) =>
      buffer.getChannelData(c),
    );
    const win = Math.max(1, Math.round(WINDOW_S * buffer.sampleRate));
    const db: number[] = [];
    for (let i = 0; i < n; i += win) {
      let sum = 0;
      const end = Math.min(n, i + win);
      for (let k = i; k < end; k++) {
        let v = 0;
        for (const ch of channels) v += ch[k];
        v /= channels.length;
        sum += v * v;
      }
      const rms = Math.sqrt(sum / (end - i));
      db.push(20 * Math.log10(rms + 1e-9));
    }
    // Silence is what sits near this file's noise floor (10 dB above its
    // quietest tenth), and always at least 20 dB under its loud parts and
    // never louder than -55 dB. The 20 dB cap matters for audio without real
    // gaps: in continuous singing the quietest tenth is still singing, and
    // the floor rule alone would cut much of it.
    const sorted = [...db].sort((a, b) => a - b);
    const at = (p: number) => sorted[Math.floor((sorted.length - 1) * p)];
    const threshold = Math.max(-55, Math.min(at(0.1) + 10, at(0.95) - 20));
    const step = win / buffer.sampleRate;
    const silent: Range[] = [];
    let runStart: number | null = null;
    db.forEach((v, i) => {
      if (v < threshold) {
        if (runStart === null) runStart = i;
      } else if (runStart !== null) {
        silent.push([runStart * step, i * step]);
        runStart = null;
      }
    });
    if (runStart !== null) silent.push([runStart * step, db.length * step]);
    const duration = buffer.duration;
    const quiet = silent
      .filter(([s, e]) => e - s >= MIN_SILENCE_S)
      .map(
        ([s, e]): Range => [
          s === 0 ? 0 : s + PAD_S,
          e >= duration - step ? duration : e - PAD_S,
        ],
      )
      .filter(([s, e]) => e > s);

    const peaks: number[] = [];
    const per = Math.max(1, Math.floor(n / PEAK_BARS));
    for (let b = 0; b < PEAK_BARS && b * per < n; b++) {
      let max = 0;
      for (let k = b * per; k < Math.min(n, (b + 1) * per); k += 4)
        max = Math.max(max, Math.abs(channels[0][k]));
      peaks.push(max);
    }
    const top = Math.max(...peaks, 0.001);
    return { duration, peaks: peaks.map((p) => p / top), silent: quiet };
  } catch (e) {
    console.warn("train: couldn't analyse file", e);
    return null;
  }
}

/** Sorted, overlapping ranges joined. */
export function merge(ranges: Range[]): Range[] {
  const out: Range[] = [];
  for (const [s, e] of [...ranges].sort((a, b) => a[0] - b[0])) {
    const last = out[out.length - 1];
    if (last && s <= last[1]) last[1] = Math.max(last[1], e);
    else out.push([s, e]);
  }
  return out;
}

/** [0, duration] without the cut ranges, dropping slivers. */
export function kept(duration: number, cut: Range[]): Range[] {
  const out: Range[] = [];
  let at = 0;
  for (const [s, e] of merge(cut)) {
    if (s > at) out.push([at, Math.min(s, duration)]);
    at = Math.max(at, e);
  }
  if (at < duration) out.push([at, duration]);
  return out.filter(([s, e]) => e - s >= MIN_KEEP_S);
}

export const secsOf = (ranges: Range[]) =>
  ranges.reduce((t, [s, e]) => t + (e - s), 0);

export type Clip = { analysis: Analysis | null; cuts: Range[] };

/** What's kept of a file: everything if it couldn't be analysed. */
export function keepOf(clip: Clip | undefined, duration: number): Range[] {
  if (!clip?.analysis) return [[0, duration]];
  return kept(clip.analysis.duration, [...clip.analysis.silent, ...clip.cuts]);
}

export const clock = (s: number) => {
  const t = Math.max(0, Math.round(s));
  const h = Math.floor(t / 3600);
  const m = Math.floor((t % 3600) / 60);
  const sec = String(t % 60).padStart(2, "0");
  return h ? `${h}:${String(m).padStart(2, "0")}:${sec}` : `${m}:${sec}`;
};

// The kept ranges as the training job takes them: sorted, inside the file,
// at most 2,000. null when they're unusable or cover (nearly) the whole file,
// which trains on the whole file as before.
export function cleanKeep(
  keep: unknown,
  duration: number,
): [number, number][] | null {
  if (!Array.isArray(keep) || keep.length === 0 || keep.length > 2000)
    return null;
  const limit =
    Number.isFinite(duration) && duration > 0 ? duration + 1 : Infinity;
  const ranges: [number, number][] = [];
  for (const r of keep) {
    if (!Array.isArray(r) || r.length !== 2) return null;
    const [s, e] = r.map(Number);
    if (
      !Number.isFinite(s) ||
      !Number.isFinite(e) ||
      s < 0 ||
      e <= s ||
      e > limit
    )
      return null;
    ranges.push([Math.round(s * 1000) / 1000, Math.round(e * 1000) / 1000]);
  }
  ranges.sort((a, b) => a[0] - b[0]);
  for (let i = 1; i < ranges.length; i++)
    if (ranges[i][0] < ranges[i - 1][1]) return null;
  const total = ranges.reduce((t, [s, e]) => t + (e - s), 0);
  if (Number.isFinite(limit) && total >= (limit - 1) * 0.995) return null;
  return ranges;
}

// Same audio added twice. Across voices trained 2026-09-09..10-09, 56% of
// non-farm datasets held at least one exact repeat (91% of the repeats
// confirmed by identical DNSMOS scores in input_stats.json), 39% of all
// uploaded seconds were repeats, and 35% of voices only reached the old 6:00
// minimum because of them. A repeat teaches the voice nothing new and costs
// training time, so a file whose bytes we already have is not added.
//
// Small files are hashed whole. Bigger ones by their size, first and last MiB
// and 32 slices spread through the middle, so a phone reads at most ~4 MiB
// per file however long it is. The middle slices matter: two takes exported
// from the same DAW session have the same length (so the same size) and often
// the same silent intro and outro, and must not look like one file.
// Without SubtleCrypto (plain-http pages, old browsers) a JS hash of the same
// bytes stands in; only a read error gives up (null), and then the file is
// simply added - a missed repeat costs training time, a false one a sample.
const WHOLE_BYTES = 4 * 1024 * 1024;
const EDGE_BYTES = 1024 * 1024;
const SLICES = 32;
const SLICE_BYTES = 64 * 1024;

function sampled(file: Blob): Blob {
  if (file.size <= WHOLE_BYTES) return file;
  const parts: Blob[] = [file.slice(0, EDGE_BYTES)];
  const span = file.size - 2 * EDGE_BYTES - SLICE_BYTES;
  for (let i = 0; i < SLICES; i++) {
    const at = EDGE_BYTES + Math.floor((span * (i + 0.5)) / SLICES);
    parts.push(file.slice(at, at + SLICE_BYTES));
  }
  parts.push(file.slice(file.size - EDGE_BYTES));
  return new Blob(parts);
}

// cyrb53: a fast 53-bit non-cryptographic hash, run twice with different
// seeds. Only used when crypto.subtle is missing.
function cyrb53(bytes: Uint8Array, seed: number): string {
  let h1 = 0xdeadbeef ^ seed;
  let h2 = 0x41c6ce57 ^ seed;
  for (let i = 0; i < bytes.length; i++) {
    h1 = Math.imul(h1 ^ bytes[i], 2654435761);
    h2 = Math.imul(h2 ^ bytes[i], 1597334677);
  }
  h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507);
  h1 ^= Math.imul(h2 ^ (h2 >>> 13), 3266489909);
  h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507);
  h2 ^= Math.imul(h1 ^ (h1 >>> 13), 3266489909);
  return (4294967296 * (2097151 & h2) + (h1 >>> 0)).toString(16);
}

export async function fingerprint(file: Blob): Promise<string | null> {
  try {
    const bytes = new Uint8Array(await sampled(file).arrayBuffer());
    const subtle = globalThis.crypto?.subtle;
    let hash: string;
    if (subtle) {
      const d = await subtle.digest("SHA-256", bytes);
      hash = Array.from(new Uint8Array(d), (x) =>
        x.toString(16).padStart(2, "0"),
      ).join("");
    } else {
      hash = `js-${cyrb53(bytes, 1)}${cyrb53(bytes, 2)}`;
    }
    return `${file.size}:${hash}`;
  } catch (e) {
    console.warn("train: couldn't fingerprint file", e);
    return null;
  }
}
